import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  CommandShortcut,
} from "../components/ui/command";
import {
  FileText, CalendarClock, Briefcase, TrendingUp, PoundSterling, User,
} from "lucide-react";
import { API } from "../config/api";
import { useDirectorRole } from "../hooks/useDirectorRole";

// Vanessa, 26 Sep 2026: "we don't need actions in the palette, they are
// easy enough to get to. basically anything that is in the left hand menu
// doesn't really need to be there." So: no Actions group at all (every
// action already has its own obvious entry point), and Navigate only
// carries what Sidebar.jsx does NOT already show — cross-checked against
// Sidebar.jsx's NAV_GROUPS at the time of writing. Home/Dashboard, Credit
// Control, Signatures, Sent Email Log, Post Hub, Completions Book and
// Bonsai Pen are all one click away in the left menu and were dropped for
// exactly that reason. If a page moves out of the sidebar, it belongs
// back here.
const NAV_COMMANDS = [
  { id: "matters", label: "Matters Overview", desc: "All open matters", icon: Briefcase, path: "/matters", group: "navigate", keywords: "matters overview open" },
  { id: "deadlines", label: "Deadlines Tracker", desc: "Calendar & deadline view", icon: CalendarClock, path: "/deadlines", group: "navigate", keywords: "deadlines calendar tracker" },
  { id: "directors", label: "Directors Lounge", desc: "Analytics & insights", icon: TrendingUp, path: "/directors", group: "navigate", keywords: "directors lounge analytics tools data health" },
  { id: "intake", label: "Intake Pipeline", desc: "Pre-open staging", icon: FileText, path: "/intake-preopen", group: "navigate", keywords: "intake pipeline preopen staging" },
  { id: "sop", label: "SOP & Procedures", desc: "Standard operating procedures", icon: FileText, path: "/sop", group: "navigate", keywords: "sop procedures standard operating" },
];

export default function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { isDirector } = useDirectorRole();
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);

  // Director-only nav entries are filtered out for everyone else. The
  // "directors" (Directors Lounge) entry routes to /directors (BM-H626),
  // which is itself director-guarded at the page level.
  const navCommands = NAV_COMMANDS.filter(
    (cmd) => cmd.id !== "directors" || isDirector,
  );

  useEffect(() => {
    if (searchQuery.length < 3) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/submissions`, { params: { search: searchQuery } });
        const items = (Array.isArray(res.data) ? res.data : [])
          .filter(s => !s.is_test_submission && s.status !== "deleted")
          .slice(0, 8)
          .map(s => ({
            id: s.id,
            label: s.primary_label || s.data?.Q1 || "Unknown",
            desc: s.matter_reference || s.secondary_label || s.form_type || "",
            type: s.form_type,
          }));
        setSearchResults(items);
      } catch {
        setSearchResults([]);
      }
      setSearching(false);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSelect = useCallback((command) => {
    onOpenChange(false);
    setSearchQuery("");
    if (command.action === "bonsai-mail") {
      window.open("/bonsai", "_blank");
    } else if (command.path) {
      navigate(command.path);
    }
  }, [navigate, onOpenChange]);

  const handleSelectResult = useCallback((result) => {
    onOpenChange(false);
    setSearchQuery("");
    navigate(`/submission/${result.id}`);
  }, [navigate, onOpenChange]);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Jump to a page or search matters..."
        value={searchQuery}
        onValueChange={setSearchQuery}
        data-testid="command-palette-input"
      />
      <CommandList>
        <CommandEmpty>{searching ? "Searching..." : "No results found."}</CommandEmpty>

        {searchResults.length > 0 && (
          <CommandGroup heading="Matters & Submissions">
            {searchResults.map(r => (
              <CommandItem
                key={r.id}
                value={`result-${r.label}-${r.desc}`}
                onSelect={() => handleSelectResult(r)}
                data-testid={`cmd-result-${r.id}`}
              >
                <User className="w-4 h-4 text-teal" />
                <div className="flex flex-col">
                  <span className="text-sm">{r.label}</span>
                  <span className="text-xs text-muted-foreground">{r.desc}</span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {searchResults.length > 0 && <CommandSeparator />}

        <CommandGroup heading="Navigate">
          {navCommands.map(cmd => (
            <CommandItem
              key={cmd.id}
              value={`${cmd.label} ${cmd.keywords}`}
              onSelect={() => handleSelect(cmd)}
              data-testid={`cmd-nav-${cmd.id}`}
            >
              <cmd.icon className="w-4 h-4 text-teal" />
              <span>{cmd.label}</span>
              <CommandShortcut className="text-[10px] text-slate-500">{cmd.desc}</CommandShortcut>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
